import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Evaluation } from '@/types/evaluation.types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Lock, AlertTriangle } from 'lucide-react';

interface ConcludeDialogProps {
  evaluation: Evaluation;
  onConclude: (evaluation: Evaluation) => void;
  children: React.ReactNode;
}

export function ConcludeDialog({
  evaluation,
  onConclude,
  children,
}: ConcludeDialogProps) {
  const { t } = useTranslation('dashboard');
  const [open, setOpen] = useState(false);

  const handleConclude = () => {
    onConclude(evaluation);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-purple-600" aria-hidden="true" />
            {t('status.conclude.title')}
          </DialogTitle>
          <DialogDescription>{t('status.conclude.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning message */}
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-4 flex items-start gap-3">
            <AlertTriangle
              className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
              aria-hidden="true"
            />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {t('status.conclude.warning')}
              </p>
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Participants will no longer be able to edit or submit scorecards.
              </p>
            </div>
          </div>

          {/* Information */}
          <div className="rounded-md bg-muted p-4">
            <p className="text-sm text-muted-foreground">
              You can reactivate this evaluation later if you need to make changes or
              allow additional scoring.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConclude}>
            <Lock className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('status.conclude.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
